import Color from '@/Enums/Color.enum';
import FractionDigits from '@/Enums/Localization/FractionDigits.enum';
import { numberToPercent } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { ChartType, Plugin } from 'chart.js';
import { AnyObject } from 'chart.js/dist/types/basic';
import classNames from 'classnames';

interface ILegendPluginOptions {
  fractionDigits?: number;
}

const createColorBox = (item: any, className?: string): HTMLElement => {
  const colorBox = document.createElement('span');
  colorBox.className = classNames('rounded-[2px] min-w-[14px] min-h-[14px]', className);
  colorBox.style.background = item.fillStyle as string;
  colorBox.style.borderColor = item.strokeStyle as string;
  colorBox.style.borderWidth = item.lineWidth + 'px';
  return colorBox;
};

/**https://www.chartjs.org/docs/latest/samples/legend/html.html */
export const htmlLegendPlugin = <TType extends ChartType>(
  pluginOptions?: ILegendPluginOptions,
): Plugin<TType, AnyObject> => ({
  id: 'htmlLegend',
  afterUpdate(chart, _args, options) {
    const container = document.getElementById(options.containerID);

    if (!container) {
      return;
    }

    while (container.firstChild) {
      container.firstChild.remove();
    }

    const items = chart.options?.plugins?.legend?.labels?.generateLabels?.(chart);
    const summary = (chart.data.datasets?.[0]?.data || []).reduce(
      (acc, val) => (acc as number) + (val as number),
      0,
    ) as number;

    items?.forEach((item, i) => {
      const legendItem = document.createElement('div');

      legendItem.onclick = () => {
        //@ts-expect-error faulty type
        const type = chart.config?.type;
        if (type === 'pie' || type === 'doughnut') {
          chart.toggleDataVisibility(item.index as number);
        } else {
          const index = item.datasetIndex as number;
          chart.setDatasetVisibility(index, !chart.isDatasetVisible(index));
        }
        chart.update();
      };

      switch (options.variant) {
        case 'pace': {
          legendItem.className = 'col-span-1 cursor-pointer';

          const firstRow = document.createElement('div');
          firstRow.className = 'flex items-center gap-[11px]';
          const colorBox = createColorBox(item);

          firstRow.appendChild(colorBox);

          const legendTextContainer = document.createElement('div');
          legendTextContainer.className = classNames('m-0 p-0 font-medium text-[14px]', {
            'line-through': item.hidden,
          });
          legendItem.style.color = Color.Grey;
          const legendText = document.createTextNode(item.text);
          legendTextContainer.appendChild(legendText);

          firstRow.appendChild(legendTextContainer);

          const valueTextContainer = document.createElement('div');
          valueTextContainer.className = classNames('ml-[25px] text-[32px] text-[#12131A]', {
            'line-through': item.hidden,
          });
          const valueText = document.createTextNode(`${chart.data.datasets?.[0]?.data?.[i] ?? ''}`);
          valueTextContainer.appendChild(valueText);

          legendItem.appendChild(firstRow);
          legendItem.appendChild(valueTextContainer);
          container.appendChild(legendItem);
          break;
        }
        case 'attribution': {
          legendItem.className = 'col-span-1 cursor-pointer';
          const firstRow = document.createElement('div');
          firstRow.className = 'flex items-center gap-[11px]';
          const colorBox = createColorBox(item);

          firstRow.appendChild(colorBox);

          const legendTextContainer = document.createElement('div');
          legendTextContainer.className = classNames('m-0 p-0 font-medium text-[14px]', {
            'line-through': item.hidden,
          });
          legendItem.style.color = Color.Grey;
          legendTextContainer.textContent = item.text;

          firstRow.appendChild(legendTextContainer);

          const valueTextContainer = document.createElement('div');
          valueTextContainer.className = classNames('ml-[25px] pt-[10px] text-[28px] text-[#12131A]', {
            'line-through': item.hidden,
          });

          valueTextContainer.textContent = numberToPercent((chart.data.datasets?.[0]?.data?.[i] as number) / summary, {
            maximumFractionDigits: pluginOptions?.fractionDigits ?? FractionDigits.Extended,
          });

          legendItem.appendChild(firstRow);
          legendItem.appendChild(valueTextContainer);
          container.appendChild(legendItem);
          break;
        }
        default: {
          let sum = 0;
          chart.data.datasets?.[0]?.data.forEach(val => (sum += val as number));
          const raw = chart.data.datasets?.[0]?.data?.[i] as number;
          const percentage = sum
            ? numberToPercent(raw / sum, {
                maximumFractionDigits: pluginOptions?.fractionDigits ?? FractionDigits.Extended,
              })
            : '0%';

          legendItem.className = 'flex items-center cursor-pointer';

          const colorBox = createColorBox(item, 'mr-[7px]');

          // Text
          const textContainer = document.createElement('p');
          textContainer.className = classNames('m-0 p-0 font-medium text-[14px]', { 'line-through': item.hidden });

          const text = document.createTextNode(item.text);
          textContainer.appendChild(text);

          // Values from data for printing
          const valueTextContainer = document.createElement('div');
          valueTextContainer.className = classNames('ml-[25px] text-small hidden print:block', {
            'line-through': item.hidden,
          });
          const valueText = document.createTextNode(raw?.toLocaleString('en-US', { maximumFractionDigits: 0 }));
          valueTextContainer.appendChild(valueText);

          const valuePercentageTextContainer = document.createElement('div');
          valuePercentageTextContainer.className = classNames('ml-[25px] text-small hidden print:block', {
            'line-through': item.hidden,
          });
          const valuePercentageText = document.createTextNode(percentage);
          valuePercentageTextContainer.appendChild(valuePercentageText);

          legendItem.appendChild(colorBox);
          legendItem.appendChild(textContainer);
          legendItem.appendChild(valueTextContainer);
          legendItem.appendChild(valuePercentageTextContainer);
          container.appendChild(legendItem);
          break;
        }
      }
    });
  },
});
